
import React, { ReactNode, useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LogOut, RefreshCw } from "lucide-react";

type ProtectedRouteProps = {
  children: ReactNode;
  adminOnly?: boolean;
};

const ProtectedRoute = ({ children, adminOnly = false }: ProtectedRouteProps) => {
  const { user, isLoading, profile, isAdmin, forceLogout, refreshProfile } = useAuth();
  const location = useLocation();
  const [showForceLogout, setShowForceLogout] = useState(false);
  const [profileLoadAttempts, setProfileLoadAttempts] = useState(0);
  const loggingRef = useRef(false);
  
  // Show force logout button after 5 seconds of loading
  useEffect(() => {
    let timeoutId: number | undefined;
    
    if (isLoading) {
      timeoutId = window.setTimeout(() => {
        setShowForceLogout(true);
      }, 5000);
    } else {
      setShowForceLogout(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading]);

  // Handle missing profile for authenticated users
  useEffect(() => {
    let timeoutId: number | undefined;
    
    if (user && !profile && !isLoading && profileLoadAttempts < 3) {
      if (!loggingRef.current) {
        console.log(`ProtectedRoute: User exists but profile missing, attempting refresh #${profileLoadAttempts + 1}`);
        loggingRef.current = true;
      }
      
      timeoutId = window.setTimeout(() => {
        refreshProfile();
        setProfileLoadAttempts(prev => prev + 1);
      }, 1000 * (profileLoadAttempts + 1)); // Exponential backoff
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, profile, isLoading, refreshProfile, profileLoadAttempts]);

  // Log route information only in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("ProtectedRoute mounted for path:", location.pathname);
      console.log("Auth state on mount:", { 
        isLoading, 
        isAuthenticated: !!user, 
        profileExists: !!profile,
        adminOnly
      });
    }
    
    return () => {
      if (import.meta.env.DEV) {
        console.log("ProtectedRoute unmounting from path:", location.pathname);
      }
    };
  }, [location.pathname, user, isLoading, profile, adminOnly]);

  // Handle profile refresh
  const handleRefreshProfile = () => {
    if (user) {
      toast.info("Refreshing profile data...");
      refreshProfile();
      setProfileLoadAttempts(prev => prev + 1);
    }
  };
  
  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4">Verifying authentication...</p>
          
          {showForceLogout && (
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2">
                Authentication is taking longer than expected.
              </p>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => {
                  toast.info("Logging you out...");
                  forceLogout();
                }}
              >
                <LogOut size={16} />
                Force Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Check if user account is suspended/deleted
  if (profile?.is_deleted) {
    toast.error("Your account has been suspended. Please contact support.");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If we need to check admin access but profile is not loaded yet
  if (adminOnly && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4">Verifying admin privileges...</p>
          
          {profileLoadAttempts > 0 && (
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2">
                Profile loading is taking longer than expected.
              </p>
              <div className="flex gap-2 justify-center mt-2">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={handleRefreshProfile}
                >
                  <RefreshCw size={16} />
                  Retry Loading
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => {
                    toast.info("Logging you out...");
                    forceLogout();
                  }}
                >
                  <LogOut size={16} />
                  Force Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If admin route but user is not admin, redirect to home
  if (adminOnly) {
    // Check both the profile.is_admin flag and the isAdmin from context
    const isUserAdmin = !!profile?.is_admin || isAdmin === true;
    
    if (profile && !isUserAdmin) {
      toast.error("Admin access required for this page");
      return <Navigate to="/" replace />;
    }
  }

  // Render the children components
  return <>{children}</>;
};

export default React.memo(ProtectedRoute);
